import React, { createContext, useContext } from 'react';
type Language = 'en' | 'es';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
export const LanguageProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const language: Language = 'en';
  const setLanguage = (lang: Language) => {
    // Language switching disabled
  };
  const t = (key: string): string => {
    return translations[language][key] || key;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-306">
      {children}
    </LanguageContext.Provider>;
};
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    'header.benefits': 'Benefits',
    'header.testimonials': 'Testimonials',
    'header.pricing': 'Pricing',
    'header.faq': 'FAQ',
    'header.buyNow': 'Buy Now',
    // Hero
    'hero.badge': '🎉 Special Offer - Up to 50% OFF',
    'hero.title': 'The Smart Ball That Exhausts, Calms and Makes Your Dog Happy...',
    'hero.subtitle': 'While You Finally Rest',
    'hero.description': "You are seconds away from transforming your dog's behavior, energy and happiness. The BranRok™ Jump Ball keeps your pet active, entertained and mentally focused, without you having to lift a finger.",
    'hero.benefitsTitle': '✨ Direct and Immediate Benefits',
    'hero.benefit1': 'Reduces anxiety and stress',
    'hero.benefit2': 'Moves on its own (real stimulation)',
    'hero.benefit3': 'Exhausts accumulated energy',
    'hero.benefit4': 'Prevents bites to furniture, shoes and objects',
    'hero.benefit5': '100% safe + premium materials',
    'hero.benefit6': 'Suitable for all ages, sizes and energy levels',
    'hero.cta': 'Yes, I want a Happy, Calm and Entertained Dog Today',
    // Problem
    'problem.header': 'WHY YOUR DOG IS FRUSTRATED (AND SO ARE YOU)',
    'problem.title': 'Your Dog Doesn\'t "Misbehave"... They\'re Bored, Anxious and Unstimulated',
    'problem.subtitle': 'And nobody warns you.',
    'problem.issuesTitle': 'Most dogs today suffer from:',
    'problem.issue1': 'Accumulated energy',
    'problem.issue2': 'Extreme boredom',
    'problem.issue3': 'Anxiety from lack of stimulation',
    'problem.issue4': 'Destruction of objects',
    'problem.issue5': 'Constant barking and restless behavior',
    'problem.text1': "It's not your fault. It's not your dog's fault.",
    'problem.text2': "It's the fault of traditional toys. They chew them for 5 minutes and lose interest.",
    'problem.text3': 'Your dog needs movement, challenge and mental stimulation...',
    'problem.text4': 'Not a static and boring ball.',
    // Solution
    'solution.header': 'MEET THE SOLUTION: THE BALL THAT PLAYS ALONE WITH YOUR DOG',
    'solution.title': 'BranRok™ Jump Ball – The Smart Ball That Revolutionized Canine Play',
    'solution.intro': 'Designed to keep your dog:',
    'solution.benefit1': 'Active',
    'solution.benefit2': 'Mentally stimulated',
    'solution.benefit3': 'Healthily tired',
    'solution.benefit4': 'Happier than ever',
    'solution.outro': 'And all... without effort on your part.',
    'solution.howTitle': 'How does it work?',
    'solution.feature1Title': 'Smart and Unpredictable Movement',
    'solution.feature1Desc': 'Detects the dog\'s approach and bounces alone in random directions. Simulates a "real prey" that activates their natural instincts.',
    'solution.feature2Title': 'Reduces Anxiety & Stress',
    'solution.feature2Desc': 'Promotes the release of dopamine and endorphins: the chemicals of canine happiness.',
    'solution.feature3Title': 'Real Tiredness in Minutes',
    'solution.feature3Desc': '15–20 minutes of play = 1 hour of traditional exercise.',
    'solution.feature4Title': 'Premium, Soft and Ultra Resistant Material',
    'solution.feature4Desc': 'Safe, durable and suitable for intense biting.',
    'solution.feature5Title': 'For All Breeds and Ages',
    'solution.feature5Desc': 'Puppies, adults, large dogs, high energy dogs... they all love it.',
    'solution.cta': 'I Want a Jump Ball for My Dog',
    // Science
    'science.header': 'THE SCIENCE OF CANINE HAPPINESS',
    'science.intro': 'Trainers agree:',
    'science.formula': 'Unpredictable movement + Mental stimulation = Balanced, healthy and happy dog.',
    'science.whenTitle': 'When your dog chases an object that seems "alive":',
    'science.benefit1': 'Reduces cortisol (stress)',
    'science.benefit2': 'Increases dopamine (pleasure)',
    'science.benefit3': 'Prevents accumulated anxiety',
    'science.benefit4': 'Burns excess energy',
    'science.benefit5': 'Improves sleep',
    'science.benefit6': 'Reduces destructive behaviors',
    'science.outro': "That's why your dog OBSESSES over the Jump Ball. And that's why they end the day calmer, more relaxed and emotionally stable.",
    // Benefits
    'benefits.header': 'BRANROK™ BENEFITS (THE "BLISSY" OF DOGS)',
    'benefits.benefit1Title': 'Zero Boredom',
    'benefits.benefit1Desc': 'Always has something stimulating to do.',
    'benefits.benefit2Title': 'Less Destruction',
    'benefits.benefit2Desc': 'Reduces bites to furniture, cables, shoes and objects.',
    'benefits.benefit3Title': 'Lower Anxiety',
    'benefits.benefit3Desc': 'Releases energy naturally and healthily.',
    'benefits.benefit4Title': 'Regulated Energy',
    'benefits.benefit4Desc': 'A tired dog = a happy, balanced and sociable dog.',
    'benefits.benefit5Title': 'More Time For You',
    'benefits.benefit5Desc': 'You can cook, work or rest while your dog plays alone.',
    'benefits.benefit6Title': 'Better Connection',
    'benefits.benefit6Desc': 'Less stress for them = more love and serenity for you.',
    'benefits.cta': "I Want My Dog's Transformation",
    // Testimonials
    'testimonials.header': 'REAL TESTIMONIALS',
    'testimonials.testimonial1': 'My dog had never been so entertained. Before he broke everything. Now he plays alone for 30-40 minutes. Saved my furniture.',
    'testimonials.testimonial2': 'I have a hyperactive labrador. This ball is THE ONLY thing that really tires him out. Mandatory purchase.',
    'testimonials.testimonial3': 'I work from home. This ball literally saved my life. My dog loves it... I love it more.',
    // Pricing
    'pricing.buyGetFree': 'Buy 3 Get 1 Free',
    'pricing.save55': 'Save 55%',
    'pricing.bestSeller': 'Best Seller',
    'pricing.familyPack': 'Family Pack',
    'pricing.duoPack': 'Duo Pack',
    'pricing.soloPack': 'Solo Pack',
    'pricing.jumpBall': 'Jump Ball',
    'pricing.sameProduct': 'Same product',
    'pricing.each': '/each',
    'pricing.orig': 'Orig',
    'pricing.total': 'Total',
    'pricing.feature1': 'Premium Ultra Resistant Material',
    'pricing.feature2': 'Smart Movement',
    'pricing.feature3': 'For All Breeds',
    'pricing.feature4': 'Rechargeable Battery',
    'pricing.sellOutRisk': 'Sell-Out Risk',
    'pricing.high': 'High',
    'pricing.freeShipping': 'FREE SHIPPING',
    'pricing.discount': 'Discount',
    'pricing.popularity': 'Popularity',
    'pricing.selected': 'Selected',
    'pricing.selectPackage': 'Select Package',
    'pricing.cta': 'Take Advantage of Special Offer Today',
    // Guarantee
    'guarantee.header': '"HAPPY DOG" 30-DAY GUARANTEE',
    'guarantee.title': 'Your peace of mind is our #1 priority.',
    'guarantee.description': "If your dog doesn't LOVE their Jump Ball, you can return it within 30 days and we'll refund 100% — no questions asked.",
    // FAQ
    'faq.header': 'FREQUENTLY ASKED QUESTIONS (FAQ)',
    'faq.q1': 'Is it safe?',
    'faq.a1': 'Yes. Non-toxic and ultra resistant materials.',
    'faq.q2': 'Does it work for large dogs?',
    'faq.a2': 'Yes. Works for small, medium and large breed dogs.',
    'faq.q3': 'What if my dog destroys everything?',
    'faq.a3': 'The Jump Ball is designed precisely for intense biting.',
    'faq.q4': 'Does it work on floor or grass?',
    'faq.a4': 'Both. Indoor and outdoor.',
    'faq.q5': 'How long does the battery last?',
    'faq.a5': '3 to 6 hours depending on use.',
    'faq.q6': "What if they don't like it?",
    'faq.a6': 'Full refund. No explanations.',
    // Footer
    'footer.finalCta': 'Give your dog the balanced, active and happy life they deserve.',
    'footer.finalDescription': 'Transform their energy, behavior and emotional well-being... in just minutes a day.',
    'footer.finalButton': "Improve My Dog's Life Today",
    'footer.description': 'The smart ball that revolutionizes canine happiness.',
    'footer.quickLinks': 'Quick Links',
    'footer.information': 'Information',
    'footer.contact': 'Contact',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.termsConditions': 'Terms and Conditions',
    'footer.returnPolicy': 'Return Policy',
    'footer.shipping': 'Shipping',
    'footer.copyright': '© 2024 BranRock. All rights reserved.'
  },
  es: {
    // Header
    'header.benefits': 'Beneficios',
    'header.testimonials': 'Testimonios',
    'header.pricing': 'Precios',
    'header.faq': 'FAQ',
    'header.buyNow': 'Comprar Ahora',
    // Hero
    'hero.badge': '🎉 Oferta Especial - Hasta 50% OFF',
    'hero.title': 'La Pelota Inteligente Que Agota, Calma y Hace Feliz a Tu Perro...',
    'hero.subtitle': 'Mientras Vos Finalmente Descansás',
    'hero.description': 'Estás a segundos de transformar el comportamiento, la energía y la felicidad de tu perro. La BranRok™ Jump Ball mantiene a tu mascota activa, entretenida y mentalmente enfocada, sin que tengas que mover un dedo.',
    'hero.benefitsTitle': '✨ Beneficios Directos e Inmediatos',
    'hero.benefit1': 'Reduce la ansiedad y el estrés',
    'hero.benefit2': 'Se mueve sola (estimulación real)',
    'hero.benefit3': 'Agota energía acumulada',
    'hero.benefit4': 'Evita mordidas a muebles, zapatillas y objetos',
    'hero.benefit5': '100% segura + materiales premium',
    'hero.benefit6': 'Apta para todas las edades, tamaños y niveles de energía',
    'hero.cta': 'Sí, quiero un Perro Feliz, Calmado y Entretenido Hoy',
    // Problem
    'problem.header': 'POR QUÉ TU PERRO ESTÁ FRUSTRADO (Y VOS TAMBIÉN)',
    'problem.title': 'Tu Perro No Se "Porta Mal"... Está Aburrido, Ansioso y Sin Estímulo',
    'problem.subtitle': 'Y nadie te lo advierte.',
    'problem.issuesTitle': 'La mayoría de los perros hoy sufren:',
    'problem.issue1': 'Energía acumulada',
    'problem.issue2': 'Aburrimiento extremo',
    'problem.issue3': 'Ansiedad por falta de estímulos',
    'problem.issue4': 'Destrucción de objetos',
    'problem.issue5': 'Ladridos constantes y conducta inquieta',
    'problem.text1': 'No es culpa tuya. No es culpa de tu perro.',
    'problem.text2': 'Es culpa de los juguetes tradicionales. Los muerde 5 minutos y pierde interés.',
    'problem.text3': 'Tu perro necesita movimiento, desafío y estímulo mental...',
    'problem.text4': 'No una pelota estática y aburrida.',
    // Solution
    'solution.header': 'CONOCÉ LA SOLUCIÓN: LA PELOTA QUE JUEGA SOLA CON TU PERRO',
    'solution.title': 'BranRok™ Jump Ball – La Pelota Inteligente Que Revolucionó el Juego Canino',
    'solution.intro': 'Diseñada para mantener a tu perro:',
    'solution.benefit1': 'Activo',
    'solution.benefit2': 'Mentalmente estimulado',
    'solution.benefit3': 'Cansado de forma saludable',
    'solution.benefit4': 'Más feliz que nunca',
    'solution.outro': 'Y todo... sin esfuerzo de tu parte.',
    'solution.howTitle': '¿Cómo funciona?',
    'solution.feature1Title': 'Movimiento Inteligente e Impredecible',
    'solution.feature1Desc': 'Detecta el acercamiento del perro y rebota sola en direcciones aleatorias. Simula una "presa real" que activa sus instintos naturales.',
    'solution.feature2Title': 'Reduce Ansiedad & Estrés',
    'solution.feature2Desc': 'Favorece la liberación de dopamina y endorfinas: los químicos de la felicidad canina.',
    'solution.feature3Title': 'Cansancio Real en Minutos',
    'solution.feature3Desc': '15–20 minutos de juego = 1 hora de ejercicio tradicional.',
    'solution.feature4Title': 'Material Premium, Suave y Ultra Resistente',
    'solution.feature4Desc': 'Segura, duradera y apta para mordidas intensas.',
    'solution.feature5Title': 'Para Todas las Razas y Edades',
    'solution.feature5Desc': 'Cachorros, adultos, perros grandes, perros de alta energía... todos la aman.',
    'solution.cta': 'Quiero una Jump Ball para mi Perro',
    // Science
    'science.header': 'LA CIENCIA DE LA FELICIDAD CANINA',
    'science.intro': 'Los entrenadores coinciden:',
    'science.formula': 'Movimiento impredecible + Estímulo mental = Perro equilibrado, sano y feliz.',
    'science.whenTitle': 'Cuando tu perro persigue un objeto que parece "vivo":',
    'science.benefit1': 'Reduce el cortisol (estrés)',
    'science.benefit2': 'Aumenta dopamina (placer)',
    'science.benefit3': 'Evita ansiedad acumulada',
    'science.benefit4': 'Quema energía sobrante',
    'science.benefit5': 'Mejora el sueño',
    'science.benefit6': 'Reduce conductas destructivas',
    'science.outro': 'Por eso tu perro SE OBSESIONA con la Jump Ball. Y por eso termina el día más tranquilo, relajado y estable emocionalmente.',
    // Benefits
    'benefits.header': 'BENEFICIOS DE BRANROK™ (EL "BLISSY" DE LOS PERROS)',
    'benefits.benefit1Title': 'Cero Aburrimiento',
    'benefits.benefit1Desc': 'Siempre tiene algo estimulante que hacer.',
    'benefits.benefit2Title': 'Menos Destrucción',
    'benefits.benefit2Desc': 'Reduce mordidas a muebles, cables, zapatos y objetos.',
    'benefits.benefit3Title': 'Ansiedad En Baja',
    'benefits.benefit3Desc': 'Libera energía de forma natural y saludable.',
    'benefits.benefit4Title': 'Energía Regulada',
    'benefits.benefit4Desc': 'Un perro cansado = un perro feliz, equilibrado y sociable.',
    'benefits.benefit5Title': 'Más Tiempo Para Vos',
    'benefits.benefit5Desc': 'Podés cocinar, trabajar o descansar mientras tu perro juega solo.',
    'benefits.benefit6Title': 'Mejor Conexión',
    'benefits.benefit6Desc': 'Menos estrés para él = más amor y serenidad para vos.',
    'benefits.cta': 'Quiero la Transformación de Mi Perro',
    // Testimonials
    'testimonials.header': 'TESTIMONIOS REALES',
    'testimonials.testimonial1': 'Mi perro jamás había estado tan entretenido. Antes rompía todo. Ahora juega solo 30-40 minutos. Salvó mis muebles.',
    'testimonials.testimonial2': 'Tengo un labrador hiperactivo. Esta pelota es LO ÚNICO que realmente lo cansa. Compra obligada.',
    'testimonials.testimonial3': 'Trabajo desde casa. Esta bola literalmente me salvó la vida. Mi perro la ama... yo la amo más.',
    // Pricing
    'pricing.buyGetFree': 'Comprá 3 Llevá 1 Gratis',
    'pricing.save55': 'Ahorrá 55%',
    'pricing.bestSeller': 'Más Vendido',
    'pricing.familyPack': 'Pack Familiar',
    'pricing.duoPack': 'Pack Dúo',
    'pricing.soloPack': 'Pack Solo',
    'pricing.jumpBall': 'Jump Ball',
    'pricing.sameProduct': 'Mismo producto',
    'pricing.each': '/cada una',
    'pricing.orig': 'Orig',
    'pricing.total': 'Total',
    'pricing.feature1': 'Material Premium Ultra Resistente',
    'pricing.feature2': 'Movimiento Inteligente',
    'pricing.feature3': 'Para Todas las Razas',
    'pricing.feature4': 'Batería Recargable',
    'pricing.sellOutRisk': 'Riesgo de Agotamiento',
    'pricing.high': 'Alto',
    'pricing.freeShipping': 'ENVÍO GRATIS',
    'pricing.discount': 'Descuento',
    'pricing.popularity': 'Popularidad',
    'pricing.selected': 'Seleccionado',
    'pricing.selectPackage': 'Seleccionar Paquete',
    'pricing.cta': 'Aprovechar Oferta Especial Hoy',
    // Guarantee
    'guarantee.header': 'GARANTÍA "PERRO FELIZ" DE 30 DÍAS',
    'guarantee.title': 'Tu tranquilidad es nuestra prioridad #1.',
    'guarantee.description': 'Si tu perro no AMA su Jump Ball, podés devolverla dentro de 30 días y te devolvemos el 100% — sin preguntas.',
    // FAQ
    'faq.header': 'PREGUNTAS FRECUENTES (FAQ)',
    'faq.q1': '¿Es segura?',
    'faq.a1': 'Sí. Materiales no tóxicos y ultra resistentes.',
    'faq.q2': '¿Sirve para perros grandes?',
    'faq.a2': 'Sí. Funciona para perros pequeños, medianos y razas grandes.',
    'faq.q3': '¿Y si mi perro destruye todo?',
    'faq.a3': 'La Jump Ball está diseñada justamente para mordidas intensas.',
    'faq.q4': '¿Funciona en piso o pasto?',
    'faq.a4': 'En ambos. Interior y exterior.',
    'faq.q5': '¿Cuánto dura la batería?',
    'faq.a5': '3 a 6 horas según el uso.',
    'faq.q6': '¿Y si no le gusta?',
    'faq.a6': 'Reembolso completo. Sin explicaciones.',
    // Footer
    'footer.finalCta': 'Dale a tu perro la vida equilibrada, activa y feliz que se merece.',
    'footer.finalDescription': 'Transformá su energía, su comportamiento y su bienestar emocional... en solo minutos al día.',
    'footer.finalButton': 'Mejorar la Vida de Mi Perro Hoy',
    'footer.description': 'La pelota inteligente que revoluciona la felicidad canina.',
    'footer.quickLinks': 'Enlaces Rápidos',
    'footer.information': 'Información',
    'footer.contact': 'Contacto',
    'footer.privacyPolicy': 'Política de Privacidad',
    'footer.termsConditions': 'Términos y Condiciones',
    'footer.returnPolicy': 'Política de Devoluciones',
    'footer.shipping': 'Envíos',
    'footer.copyright': '© 2024 BranRock. Todos los derechos reservados.'
  }
};